/* eslint-disable react/no-children-prop */
import React, { useCallback, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { Form } from 'react-bootstrap';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { Lock, LogIn, Mail, Shield } from 'react-feather';
import {
  Box,
  Button as ChakraUIButton,
  Input,
  Flex,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import Button from '../../components/Button';

import logoImg from '../../assets/images/logo.png';
import { useAuth } from '../../hooks/auth';

import { Container, Content } from './styles';
import ErrorMessage from '../../components/ErrorMessage';

interface SignInFormData {
  email: string;
  password: string;
}

interface TwoFactorFormData {
  code: string;
}

const SignIn: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'credentials' | 'twoFactor'>('credentials');
  const [pendingEmail, setPendingEmail] = useState('');

  const credentialsSchema = useMemo(() =>
    yup.object().shape({
      email: yup.string().email().required('Campo obrigatório'),
      password: yup.string().required('Campo obrigatório'),
    }), []);

  const twoFactorSchema = useMemo(() =>
    yup.object().shape({
      code: yup
        .string()
        .length(6, 'O código deve ter 6 dígitos')
        .matches(/^\d+$/, 'Apenas números')
        .required('Campo obrigatório'),
    }), []);

  const {
    register: registerCredentials,
    handleSubmit: handleSubmitCredentials,
    formState: { errors: credentialsErrors },
  } = useForm<SignInFormData>({ resolver: yupResolver(credentialsSchema) });

  const {
    register: registerTwoFactor,
    handleSubmit: handleSubmitTwoFactor,
    formState: { errors: twoFactorErrors },
    reset: resetTwoFactor,
  } = useForm<TwoFactorFormData>({ resolver: yupResolver(twoFactorSchema) });

  const { signIn, verifyTwoFactor } = useAuth();

  const onSubmitCredentials: SubmitHandler<SignInFormData> = useCallback(
    async ({ email, password }) => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        const result = await signIn({ email, password });
        if (result.requiresTwoFactor) {
          setPendingEmail(result.email);
          resetTwoFactor({ code: '' });
          setStep('twoFactor');
        }
        // se requiresTwoFactor === false, auth state já foi setado e a rota redireciona automaticamente
      } catch (error) {
        if (error.response?.data) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [signIn],
  );

  const onSubmitCode: SubmitHandler<TwoFactorFormData> = useCallback(
    async ({ code }) => {
      try {
        setIsLoading(true);
        setErrorMessage('');
        await verifyTwoFactor({ email: pendingEmail, code });
      } catch (error) {
        if (error.response?.data) {
          setErrorMessage(error.response.data.message);
        } else {
          setErrorMessage(error.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [verifyTwoFactor, pendingEmail],
  );

  if (step === 'twoFactor') {
    return (
      <Container>
        <Content>
          <Form onSubmit={handleSubmitTwoFactor(onSubmitCode)}>
            <img src={logoImg} alt="Logo Alutal" />

            <Flex direction="column" align="center" mb={6} gap={1}>
              <Shield color="#0085ae" size={32} />
              <Text fontWeight="bold" fontSize="lg" mt={2}>
                Verificação em duas etapas
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Enviamos um código de 6 dígitos para
              </Text>
              <Text fontSize="sm" fontWeight="medium">
                {pendingEmail}
              </Text>
            </Flex>

            <FormControl>
              <FormLabel>Código de verificação</FormLabel>
              <InputGroup>
                <InputLeftElement
                  pointerEvents="none"
                  children={<Shield color="#0085ae" />}
                />
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  textAlign="center"
                  letterSpacing="0.5em"
                  fontSize="xl"
                  autoComplete="off"
                  {...registerTwoFactor('code')}
                />
              </InputGroup>
              <Box mt={2}>
                <ErrorMessage message={twoFactorErrors.code?.message} />
              </Box>
            </FormControl>

            <ErrorMessage message={errorMessage} />
            <Button type="submit" isLoading={isLoading}>
              Confirmar
            </Button>
          </Form>
          <Flex align="center" justify="center">
            <ChakraUIButton
              variant="link"
              colorScheme="gray"
              fontSize="sm"
              onClick={() => {
                setStep('credentials');
                setErrorMessage('');
              }}
            >
              Voltar para o login
            </ChakraUIButton>
          </Flex>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Content>
        <Form onSubmit={handleSubmitCredentials(onSubmitCredentials)}>
          <img src={logoImg} alt="Logo Alutal" />
          <FormControl>
            <FormLabel>E-mail</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<Mail color="#0085ae" />}
              />
              <Input type="email" {...registerCredentials('email')} />
            </InputGroup>
            <Box mt={2}>
              <ErrorMessage message={credentialsErrors.email?.message} />
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel>Senha</FormLabel>
            <InputGroup>
              <InputLeftElement
                pointerEvents="none"
                children={<Lock color="#0085ae" />}
              />
              <Input type="password" {...registerCredentials('password')} />
            </InputGroup>
            <Box mt={2}>
              <ErrorMessage message={credentialsErrors.password?.message} />
            </Box>
          </FormControl>
          <ErrorMessage message={errorMessage} />
          <Button type="submit" isLoading={isLoading}>
            Entrar
          </Button>
        </Form>
        <Flex align="center" justify="center" direction="column">
          <ChakraUIButton
            variant="link"
            alignSelf="flex-end"
            as={Link}
            to="/forgot-password"
          >
            Esqueceu a senha?
          </ChakraUIButton>
          <ChakraUIButton
            leftIcon={<LogIn />}
            variant="link"
            mt="16"
            colorScheme="blue"
            as={Link}
            to="/sign-up"
          >
            Criar conta
          </ChakraUIButton>
        </Flex>
      </Content>
    </Container>
  );
};

export default SignIn;
